import { useEffect, useRef, useState } from 'react';

export default function BrandSelect({ id, name, labelId, options, value, defaultValue, onChange }) {
  const [internal, setInternal] = useState(defaultValue ?? options[0]?.[0] ?? '');
  const selected = value ?? internal;
  const selectedIndex = Math.max(
    0,
    options.findIndex(([key]) => key === selected),
  );
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(selectedIndex);
  const [above, setAbove] = useState(false);
  const root = useRef(null);
  const trigger = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  const show = () => {
    const rect = trigger.current.getBoundingClientRect();
    setAbove(window.innerHeight - rect.bottom < 280 && rect.top > 280);
    setActive(selectedIndex);
    setOpen(true);
  };
  const choose = (index) => {
    setInternal(options[index][0]);
    onChange?.(options[index][0]);
    setOpen(false);
    trigger.current?.focus();
  };
  const keyDown = (event) => {
    const key = event.key;
    if (key === 'Tab' || key === 'Escape') {
      setOpen(false);
      if (key === 'Escape') event.preventDefault();
      return;
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) {
      event.preventDefault();
      if (!open) {
        show();
        if (key === 'Home') setActive(0);
        if (key === 'End') setActive(options.length - 1);
        return;
      }
      const next =
        key === 'Home'
          ? 0
          : key === 'End'
            ? options.length - 1
            : Math.max(0, Math.min(options.length - 1, active + (key === 'ArrowDown' ? 1 : -1)));
      setActive(next);
      document.getElementById(id + '-option-' + next)?.scrollIntoView?.({ block: 'nearest' });
    } else if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      if (open) choose(active);
      else show();
    } else if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const next = options.findIndex(
        ([, text], index) =>
          index > active && text.toLocaleLowerCase('ru').startsWith(key.toLocaleLowerCase('ru')),
      );
      const index =
        next >= 0
          ? next
          : options.findIndex(([, text]) =>
              text.toLocaleLowerCase('ru').startsWith(key.toLocaleLowerCase('ru')),
            );
      if (index >= 0) {
        event.preventDefault();
        if (!open) show();
        setActive(index);
      }
    }
  };
  return (
    <div
      className="brand-select"
      ref={root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      {name && <input type="hidden" name={name} value={selected} />}
      <button
        ref={trigger}
        type="button"
        id={id}
        role="combobox"
        aria-labelledby={labelId}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={id + '-options'}
        aria-activedescendant={open ? id + '-option-' + active : undefined}
        className="brand-select-trigger"
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={keyDown}
      >
        <span>{options[selectedIndex]?.[1]}</span>
        <span aria-hidden="true">⌄</span>
      </button>
      {open && (
        <ul
          role="listbox"
          id={id + '-options'}
          aria-labelledby={labelId}
          className={'brand-select-menu' + (above ? ' is-above' : '')}
        >
          {options.map(([key, text], index) => (
            <li key={key} role="presentation">
              <button
                type="button"
                role="option"
                id={id + '-option-' + index}
                tabIndex={-1}
                aria-selected={key === selected}
                className={'brand-select-option' + (active === index ? ' is-active' : '')}
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => choose(index)}
                onMouseEnter={() => setActive(index)}
              >
                {text}
                {key === selected && <span aria-hidden="true">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
